/**
 * Orders API client
 * Phase 5 Task 2 - Order Ingestion
 */
import { api } from '../services/api';

export interface Order {
  id: string;
  external_id: string;
  source: string;
  source_status?: string;
  fulfillment_status: string;
  commercial_status: string;
  order_timestamp: string;
  buyer_name?: string;
  buyer_email?: string;
  shipping_address?: string;
  shipping_address_json?: {
    name?: string;
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    phone?: string;
    formatted?: string;
  };
  subtotal: string;
  shipping: string;
  taxes: string;
  fees: string;
  fees_source?: string | null;
  net_proceeds?: string | null;
  total: string;
  item_count: number;
  created_at: string;
  hold_reason?: string | null;
  ban_hold?: boolean;
  label?: string | null;
  display_id?: string;
  is_repeat_customer?: boolean;
  prior_order_count?: number;
  short_picks?: OrderShortPick[];
  combined_with_order_id?: string | null;
  combined_with_label?: string | null;
}

export interface OrderShortPick {
  id: string;
  tray_id: string;
  order_id: string;
  line_id: string;
  order_item_id: string;
  kind: string;
  qty: number;
  note: string | null;
  card_name: string;
  slot_number: number | null;
  created_at: string;
}

export interface OrderLineItem {
  id: string;
  order_id: string;
  variant_id?: string;
  product_name?: string;
  external_line_id?: string;
  requested_qty: number;
  allocated_qty: number;
  shortfall_qty: number;
  unit_price: string;
  line_total: string;
  condition?: string;
  finish?: string;
  allocated_copies?: Array<{
    id: string;
    condition?: string;
    is_foil?: boolean;
    printing?: string;
    language?: string;
    status?: string;
    location_bin?: string | null;
    acquisition_cost?: number | null;
    unit_cost?: number | null;
    acquired_at?: string | null;
    sold_at?: string | null;
    sold_price?: number | null;
  }>;
  created_at: string;
  cogs?: string | number | null;
  gross_profit?: string | number | null;
  gross_margin_pct?: string | number | null;
  cogs_complete?: boolean | null;
  unit_cogs?: string | number | null;
  unit_cogs_source?: string | null;
}

export interface OrderProfit {
  order_id: string;
  product_revenue: string | number;
  cogs: string | number;
  gross_profit: string | number;
  fees: string | number;
  fees_source?: string | null;
  net_proceeds?: string | number | null;
  net_profit: string | number;
  gross_margin_pct?: string | number | null;
  cogs_complete: boolean;
}

export interface OrderListResponse {
  items: Order[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface OrderImportResult {
  success: boolean;
  total_orders: number;
  imported_count: number;
  failed_count: number;
  total_items_synced: number;
  errors: Array<{ external_id?: string; error: string }>;
}

export interface ListOrdersParams {
  page?: number;
  page_size?: number;
  source?: string;
  status_group?: 'pending' | 'completed';
  fulfillment_status?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
}

/**
 * List orders with pagination and filters
 */
export const listOrders = async (params: ListOrdersParams = {}): Promise<OrderListResponse> => {
  const response = await api.get<OrderListResponse>('/orders', { params });
  return response.data;
};

/**
 * Get a single order by UUID (same shape as list items).
 */
export const getOrder = async (orderId: string): Promise<Order> => {
  const response = await api.get<Order>(`/orders/${orderId}`);
  return response.data;
};

/**
 * Get line items for a specific order
 */
export const getOrderLineItems = async (orderId: string): Promise<OrderLineItem[]> => {
  const response = await api.get<OrderLineItem[]>(`/orders/${orderId}/line-items`);
  return response.data;
};

export const getOrderProfit = async (orderId: string): Promise<OrderProfit> => {
  const response = await api.get<OrderProfit>(`/orders/${orderId}/profit`);
  return response.data;
};

export interface EstimateIncompleteCogsResult {
  order_id: string;
  external_id: string;
  commercial_status?: string | null;
  prefer_reports: boolean;
  report_line_ids: string[];
  updated: Array<{
    line_id: string;
    card_name?: string | null;
    unit_price?: number | null;
    unit_cogs: number;
    unit_cogs_source: string;
  }>;
  skipped: Array<{
    line_id: string;
    card_name?: string | null;
    reason: string;
  }>;
  apply: boolean;
}

export interface CombineSuggestion {
  order_id: string;
  channel: string;
  external_label: string;
  buyer_name?: string | null;
  status: string;
  commercial_status: string;
  total: string | number;
  created_at: string;
  reason: string;
}

export interface CombineSuggestionsResponse {
  suggestions: CombineSuggestion[];
}

export interface MarkShippedPayload {
  tracking_number?: string;
  carrier?: string;
}

/** One side of a combine-link write. `order_id` is the SyncBase order UUID. */
export interface CombineLinkSide {
  order_id: string;
  combined_with_order_id?: string | null;
  combined_with_label?: string | null;
}

/** POST and DELETE /orders/{id}/combine-link. */
export interface CombineLinkResponse {
  order: CombineLinkSide;
  partner?: CombineLinkSide | null;
}

/**
 * Suggested package partners. Does not write a link.
 * GET /api/v1/orders/{order_id}/combine-suggestions
 */
export const fetchCombineSuggestions = async (
  orderId: string,
): Promise<CombineSuggestionsResponse> => {
  const response = await api.get<CombineSuggestionsResponse>(
    `/orders/${orderId}/combine-suggestions`,
  );
  return {
    suggestions: response.data?.suggestions ?? [],
  };
};

/**
 * Persist a package link. `confirm: true` is always sent; the UI must
 * only call this after the operator checks the confirm control.
 * POST /api/v1/orders/{order_id}/combine-link
 */
export const linkOrderCombine = async (
  orderId: string,
  partnerOrderId: string,
): Promise<CombineLinkResponse> => {
  const response = await api.post<CombineLinkResponse>(`/orders/${orderId}/combine-link`, {
    partner_order_id: partnerOrderId,
    confirm: true,
  });
  return response.data;
};

/**
 * Remove the package link from this order (and its partner, server-side).
 * DELETE /api/v1/orders/{order_id}/combine-link
 */
export const unlinkOrderCombine = async (orderId: string): Promise<CombineLinkResponse> => {
  const response = await api.delete<CombineLinkResponse>(`/orders/${orderId}/combine-link`);
  return response.data;
};

/**
 * Mark the order shipped in SyncBase. Optional tracking. This is not a
 * Manapool ship call.
 * POST /api/v1/orders/{order_id}/mark-shipped
 */
export const markOrderShipped = async (
  orderId: string,
  payload: MarkShippedPayload = {},
): Promise<Order> => {
  const body: MarkShippedPayload = {};
  const tracking = payload.tracking_number?.trim();
  const carrier = payload.carrier?.trim();
  if (tracking) {
    body.tracking_number = tracking;
  }
  if (carrier) {
    body.carrier = carrier;
  }
  const response = await api.post<Order>(`/orders/${orderId}/mark-shipped`, body);
  return response.data;
};

export const estimateIncompleteCogs = async (
  orderId: string,
  payload: { line_ids?: string[]; prefer_reports?: boolean; apply?: boolean } = {},
): Promise<EstimateIncompleteCogsResult> => {
  const response = await api.post<EstimateIncompleteCogsResult>(
    `/orders/${orderId}/estimate-incomplete-cogs`,
    payload,
  );
  return response.data;
};

/**
 * Import orders from CSV file
 */
export const importOrdersCSV = async (file: File): Promise<OrderImportResult> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post<OrderImportResult>('/orders/import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};
